/**
 * Size guide extractor
 *
 * Product descriptions on this store include size chart data at the
 * bottom - usually a real <table>, but on a few products it was pasted in
 * as plain text with manual spacing instead (leftover markup from another
 * site). Rather than showing that inline in the description, this moves it
 * into a collapsible "Size guide" accordion near the size selector -
 * matching the hidden-until-opened pattern used on reference sites. If a
 * product's description has neither, the accordion row stays hidden so
 * nothing empty is shown.
 */

// Turns plain text like "Measurement  S  M  L  XL\nLength  64.5  67  69.5  72"
// (columns separated by 2+ spaces, rows separated by newlines) into a real
// <table>, matching the same structure as this store's normal size tables.
function kincaidBuildTableFromPlainText(text) {
  var lines = text
    .split('\n')
    .map(function (line) { return line.trim(); })
    .filter(function (line) { return line.length > 0; });

  if (lines.length < 2) return null;

  var rows = lines.map(function (line) {
    return line.split(/\s{2,}/).map(function (cell) { return cell.trim(); }).filter(Boolean);
  });

  // Every row should have more than one column, or this isn't tabular data.
  if (rows[0].length < 2) return null;

  var table = document.createElement('table');

  var thead = document.createElement('thead');
  var headRow = document.createElement('tr');
  rows[0].forEach(function (cell, i) {
    var th = document.createElement('th');
    th.textContent = cell;
    if (i > 0) th.setAttribute('align', 'right');
    headRow.appendChild(th);
  });
  thead.appendChild(headRow);
  table.appendChild(thead);

  var tbody = document.createElement('tbody');
  rows.slice(1).forEach(function (rowCells) {
    var tr = document.createElement('tr');
    rowCells.forEach(function (cell, i) {
      var td = document.createElement('td');
      td.textContent = cell;
      if (i > 0) td.setAttribute('align', 'right');
      tr.appendChild(td);
    });
    tbody.appendChild(tr);
  });
  table.appendChild(tbody);

  return table;
}

// Canonical size order + the single-letter label each size should show.
// Product data across the catalog is inconsistent - some products use
// "S"/"M"/"L"/"XL", others spell it out as "small"/"medium"/"large"/"xl".
// This normalizes the *displayed* label only; the underlying option value
// (used to match the selected variant) is left untouched.
var KINCAID_SIZE_ORDER = [
  { rank: 0, label: 'XXS', match: ['xxs', 'xx-small', 'xx small'] },
  { rank: 1, label: 'XS', match: ['xs', 'x-small', 'x small', 'extra small'] },
  { rank: 2, label: 'S', match: ['s', 'small'] },
  { rank: 3, label: 'M', match: ['m', 'medium', 'med'] },
  { rank: 4, label: 'L', match: ['l', 'large'] },
  { rank: 5, label: 'XL', match: ['xl', 'x-large', 'x large', 'extra large', 'extra-large'] },
  { rank: 6, label: 'XXL', match: ['xxl', '2xl', 'xx-large', 'extra extra large'] },
  { rank: 7, label: 'XXXL', match: ['xxxl', '3xl'] }
];

function kincaidLookupSize(rawValue) {
  var normalized = (rawValue || '').trim().toLowerCase();
  for (var i = 0; i < KINCAID_SIZE_ORDER.length; i++) {
    if (KINCAID_SIZE_ORDER[i].match.indexOf(normalized) !== -1) {
      return KINCAID_SIZE_ORDER[i];
    }
  }
  return null;
}

// Relabels size button text to single letters (S/M/L/XL) and reorders the
// buttons left-to-right as XS, S, M, L, XL, XXL... regardless of the order
// the merchant originally entered them in on each product.
function kincaidNormalizeSizeButtons() {
  document.querySelectorAll('variant-radios fieldset.product-form__input').forEach(function (fieldset) {
    var legend = fieldset.querySelector('legend');
    if (!legend || legend.textContent.trim().toLowerCase().indexOf('size') === -1) return;

    var inputs = Array.prototype.slice.call(fieldset.querySelectorAll('input[type="radio"]'));
    if (inputs.length === 0) return;

    var pairs = inputs.map(function (input) {
      var label = fieldset.querySelector('label[for="' + input.id + '"]');
      var size = kincaidLookupSize(input.value);
      return { input: input, label: label, rank: size ? size.rank : 999, displayLabel: size ? size.label : null };
    });

    // Don't touch this option if it doesn't look like a size option at all
    // (none of its values matched a known size) - could be something else
    // using "size" in its legend by coincidence.
    var anyMatched = pairs.some(function (pair) { return pair.rank !== 999; });
    if (!anyMatched) return;

    pairs.forEach(function (pair) {
      if (!pair.label || !pair.displayLabel) return;
      // The label's first child is the text node holding the value; the
      // "sold out" accessibility span comes after it and must stay put.
      var textNode = pair.label.firstChild;
      if (textNode && textNode.nodeType === Node.TEXT_NODE) {
        textNode.textContent = pair.displayLabel;
      } else {
        pair.label.insertBefore(document.createTextNode(pair.displayLabel), pair.label.firstChild);
      }
    });

    pairs.sort(function (a, b) { return a.rank - b.rank; });
    pairs.forEach(function (pair) {
      fieldset.appendChild(pair.input);
      if (pair.label) fieldset.appendChild(pair.label);
    });
  });
}

// The theme's generic slider counter (the "1 / N" indicator under the
// product photo gallery) computes its total from slide widths/offsets
// rather than just counting slides. On the mobile single-slide "peek"
// layout that math can round down by one, showing e.g. "3 / 4" for a
// product that only has 3 photos. Rather than fight that pixel math, this
// watches the counter and corrects the total back to the real number of
// slides in the DOM whenever it changes - works for any photo count, on
// every product, regardless of when/how the theme's own script updates it.
function kincaidFixGalleryCounters() {
  document.querySelectorAll('media-gallery').forEach(function (gallery) {
    var viewer = gallery.querySelector('slider-component:not(.thumbnail-slider)');
    if (!viewer) return;

    var totalEl = viewer.querySelector('.slider-counter--total');
    var slideList = viewer.querySelector('ul.product__media-list');
    if (!totalEl || !slideList) return;

    function correctCount() {
      var trueTotal = slideList.querySelectorAll(':scope > li').length;
      if (trueTotal > 0 && totalEl.textContent !== String(trueTotal)) {
        totalEl.textContent = String(trueTotal);
      }
    }

    correctCount();
    new MutationObserver(correctCount).observe(totalEl, {
      childList: true,
      characterData: true,
      subtree: true
    });
  });
}

document.addEventListener('DOMContentLoaded', function () {
  kincaidNormalizeSizeButtons();
  kincaidFixGalleryCounters();

  document.querySelectorAll('[id^="ProductSizeGuide-"]').forEach(function (guide) {
    var sectionId = guide.id.replace('ProductSizeGuide-', '');
    var info = document.getElementById('ProductInfo-' + sectionId);
    if (!info) return;

    var description = info.querySelector('.product__description');
    if (!description) return;

    var table = description.querySelector('table');
    var nodeToRemoveFromDescription = null;

    if (table) {
      // Pull the table (and its wrapping element, if it's just a wrapper div)
      // out of the description and into the accordion content.
      nodeToRemoveFromDescription = table.parentElement && table.parentElement.children.length === 1
        ? table.parentElement
        : table;
    } else {
      // Fallback: on a few products the size chart was pasted in as plain
      // text (with leftover markup from wherever it was copied from)
      // instead of a real <table>. Detect that and rebuild it properly.
      var pseudo = description.querySelector('[class*="radix-state"]');
      if (pseudo) {
        var withBreaks = pseudo.innerHTML.replace(/<br\s*\/?>/gi, '\n');
        var plain = document.createElement('div');
        plain.innerHTML = withBreaks;
        var built = kincaidBuildTableFromPlainText(plain.textContent || '');
        if (built) {
          table = built;
          nodeToRemoveFromDescription = pseudo;
        }
      }
    }

    if (!table) return;

    var content = document.getElementById('SizeGuideContent-' + sectionId);
    if (!content) return;

    if (nodeToRemoveFromDescription) {
      nodeToRemoveFromDescription.remove();
    }
    content.appendChild(table);
    guide.style.display = '';
  });

  /**
   * Keep opened product accordions (Details / Size guide / Shipping &
   * returns) clear of the sticky header + ticker banner.
   *
   * The sticky header can be in a "revealed" fixed-position state at the
   * moment an accordion is opened (independent of whether the browser does
   * any scrolling), which was covering the top of the newly-opened content.
   * scroll-margin-top on the accordion (see main-product.liquid) only helps
   * once a scroll actually happens, so explicitly scroll each accordion
   * into view when it opens — this always resolves the overlap, and
   * scroll-margin-top makes sure that scroll lands below the sticky header.
   */
  document.querySelectorAll('.product__accordion details').forEach(function (details) {
    details.addEventListener('toggle', function () {
      if (!details.open) return;
      // Wait a frame so any content moved in (e.g. the size guide table)
      // has settled and the accordion's expanded height is accurate.
      requestAnimationFrame(function () {
        details.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
  });
});
